import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faviconUrl, brandingUrls } from '../../config/branding';

const VISIBLE_MS = 10000;
const VIEWPORT_MARGIN = 12;
const BANNER_WIDTH = 360;
/** 相对铃铛右缘再往右靠，贴近顶栏右侧 */
const RIGHT_NUDGE_PX = 48;

const stripHtml = (value) =>
	String(value ?? '')
		.replace(/<[^>]*>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const resolveBannerCategory = (notification) => {
	const to = String(notification?.to || '');
	const text = stripHtml(notification?.rawText || notification?.children);
	if (to.includes('/approval/customer')) return '客户';
	if (to.includes('/approval/quota')) return '额度';
	if (to.includes('/approval/final-cost')) return '最终成本';
	if (to.includes('/approval/order')) {
		if (text.includes('最终成本')) return '最终成本';
		return '订单';
	}
	if (text.includes('最终成本')) return '最终成本';
	if (text.includes('额度')) return '额度';
	if (text.includes('客户')) return '客户';
	if (text.includes('订单')) return '订单';
	return '通知';
};

const resolveBannerStatus = (emoji) => {
	if (emoji === '✅') return '审批通过';
	if (emoji === '❌') return '审批驳回';
	if (emoji === '📩') return '待审批';
	return '通知';
};

const resolveBannerTitle = (notification) => {
	const category = resolveBannerCategory(notification);
	const status = resolveBannerStatus(notification?.emoji);
	if (category === '通知' && status === '通知') return '系统 · 通知';
	return `${category} · ${status}`;
};

const NotificationBanner = ({ notification, anchorRef, forceClose, onActivate, onClosed }) => {
	const [phase, setPhase] = useState('in');
	const [pos, setPos] = useState(null);
	const remainingRef = useRef(VISIBLE_MS);
	const startedAtRef = useRef(0);
	const timerRef = useRef(null);
	const exitingRef = useRef(false);
	const onClosedRef = useRef(onClosed);
	onClosedRef.current = onClosed;

	const title = useMemo(() => resolveBannerTitle(notification), [notification]);
	const body = useMemo(() => stripHtml(notification?.rawText || notification?.children), [notification?.rawText, notification?.children]);

	const beginExit = useCallback(() => {
		if (exitingRef.current) return;
		exitingRef.current = true;
		window.clearTimeout(timerRef.current);
		setPhase('out');
	}, []);

	const arm = useCallback(
		(ms) => {
			window.clearTimeout(timerRef.current);
			startedAtRef.current = Date.now();
			timerRef.current = window.setTimeout(beginExit, ms);
		},
		[beginExit],
	);

	useEffect(() => {
		if (forceClose) {
			onClosedRef.current();
		}
	}, [forceClose]);

	useEffect(() => {
		arm(VISIBLE_MS);
		return () => window.clearTimeout(timerRef.current);
	}, [arm]);

	useEffect(() => {
		if (phase !== 'out') return undefined;
		if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
		onClosedRef.current();
		return undefined;
	}, [phase]);

	useLayoutEffect(() => {
		let rafId = 0;
		const update = () => {
			const el = anchorRef?.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			const width = Math.min(BANNER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
			const maxRight = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
			const alignedToBell = window.innerWidth - rect.right - RIGHT_NUDGE_PX;
			const right = Math.min(Math.max(VIEWPORT_MARGIN, alignedToBell), maxRight);
			const top = rect.bottom + 10;
			setPos((prev) => {
				if (prev && prev.top === top && prev.right === right && prev.width === width) return prev;
				return { top, right, width };
			});
		};
		const scheduleUpdate = () => {
			if (rafId) return;
			rafId = window.requestAnimationFrame(() => {
				rafId = 0;
				update();
			});
		};
		update();
		window.addEventListener('resize', scheduleUpdate);
		window.addEventListener('scroll', scheduleUpdate, true);
		return () => {
			if (rafId) window.cancelAnimationFrame(rafId);
			window.removeEventListener('resize', scheduleUpdate);
			window.removeEventListener('scroll', scheduleUpdate, true);
		};
	}, [anchorRef]);

	if (!pos) return null;

	return createPortal(
		<div
			className={classNames('notification-banner', phase === 'out' ? 'notification-banner--out' : 'notification-banner--in')}
			style={{ top: pos.top, right: pos.right, width: pos.width }}
			role='status'
			onMouseEnter={() => {
				if (exitingRef.current) return;
				remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
				window.clearTimeout(timerRef.current);
			}}
			onMouseLeave={() => {
				if (exitingRef.current) return;
				arm(remainingRef.current);
			}}
			onAnimationEnd={(event) => {
				if (event.target !== event.currentTarget) return;
				if (phase === 'out') onClosedRef.current();
			}}
		>
			<button
				type='button'
				className='notification-banner__close'
				aria-label='关闭'
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					beginExit();
				}}
			>
				<FontAwesomeIcon icon='times' />
			</button>
			<button
				type='button'
				className='notification-banner__main'
				onClick={() => {
					onActivate(notification);
					beginExit();
				}}
			>
				<img
					className='notification-banner__icon'
					src={brandingUrls.logoMark}
					alt=''
					width={38}
					height={38}
					onError={(event) => {
						if (event.currentTarget.src !== faviconUrl) {
							event.currentTarget.src = faviconUrl;
						}
					}}
				/>
				<span className='notification-banner__text'>
					<span className='notification-banner__title'>{title}</span>
					<span className='notification-banner__body'>{body}</span>
				</span>
			</button>
		</div>,
		document.body,
	);
};

NotificationBanner.propTypes = {
	notification: PropTypes.shape({
		_id: PropTypes.string,
		to: PropTypes.string,
		time: PropTypes.string,
		children: PropTypes.node,
		rawText: PropTypes.string,
		emoji: PropTypes.string,
	}).isRequired,
	anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
	forceClose: PropTypes.bool,
	onActivate: PropTypes.func.isRequired,
	onClosed: PropTypes.func.isRequired,
};

export default NotificationBanner;
